/**
 * useApplications Hook
 * Manages applications (modernization projects)
 */

import { useState, useEffect, useCallback } from 'react'
import { useApp } from '../context'

export function useApplications() {
  const { db } = useApp()
  const [applications, setApplications] = useState([])
  const [currentApplication, setCurrentApplication] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Load all applications
  const loadApplications = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const apps = await db.getApplications()
      setApplications(apps)
      // Set current application to first one if not set
      if (!currentApplication && apps.length > 0) {
        setCurrentApplication(apps[0])
      }
    } catch (err) {
      setError(err.message)
      console.error('Failed to load applications:', err)
    } finally {
      setLoading(false)
    }
  }, [db, currentApplication])

  // Load applications on mount
  useEffect(() => {
    loadApplications()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Get a single application by ID
  const getApplication = useCallback(async (id) => {
    try {
      return await db.getApplication(id)
    } catch (err) {
      setError(err.message)
      return null
    }
  }, [db])

  // Create a new application
  const createApplication = useCallback(async (data) => {
    setError(null)
    try {
      const app = await db.createApplication(data)
      setApplications(prev => [...prev, app])
      return app
    } catch (err) {
      setError(err.message)
      throw err
    }
  }, [db])

  // Update an application
  const updateApplication = useCallback(async (id, data) => {
    setError(null)
    try {
      const app = await db.updateApplication(id, data)
      setApplications(prev => prev.map(a => a.id === id ? app : a))
      if (currentApplication?.id === id) {
        setCurrentApplication(app)
      }
      return app
    } catch (err) {
      setError(err.message)
      throw err
    }
  }, [db, currentApplication])

  // Delete an application
  const deleteApplication = useCallback(async (id) => {
    setError(null)
    try {
      await db.deleteApplication(id)
      setApplications(prev => prev.filter(a => a.id !== id))
      if (currentApplication?.id === id) {
        setCurrentApplication(applications.find(a => a.id !== id) || null)
      }
    } catch (err) {
      setError(err.message)
      throw err
    }
  }, [db, currentApplication, applications])

  // Select an application as current
  const selectApplication = useCallback((app) => {
    setCurrentApplication(app)
  }, [])

  return {
    // State
    applications,
    currentApplication,
    loading,
    error,

    // Actions
    loadApplications,
    getApplication,
    createApplication,
    updateApplication,
    deleteApplication,
    selectApplication,
  }
}

export default useApplications
